import { useMutation } from '@apollo/client';
import { AptunoApolloLink } from '@shared/data-access';
import { ResponseApolloErrorMapping } from '@shared/data-mappers';
import { BaseMutationResponse2, Response } from '@shared/models';
import {
  CreditStudyCommandActions,
  AddProvidedUserInfoToCreditStudyCommand,
  InitializeCreditStudyCommand,
  CreditStudyId,
} from '../../../domain';
import {
  AddProvidedUserInfoToCreditStudyDto,
  AddProvidedUserInfoToCreditStudyResponseDto,
  InitializeCreditStudyResponseDto,
  mapInitializeCreditStudyCommandToDto,
} from '../dtos';
import { initializeCreditStudy } from '../schemas';
import { addUserInfoToCreditStudy } from '../schemas/mutations/add-provided-user-info-to-credit-study.mutation';
import { useMemo } from 'react';

export const GqlCreditStudyCommandActions: CreditStudyCommandActions = {
  useAddProvidedUserInfo: (): BaseMutationResponse2<
    AddProvidedUserInfoToCreditStudyCommand,
    string
  > => {
    const [action, { loading, data, error }] = useMutation<
      AddProvidedUserInfoToCreditStudyResponseDto,
      AddProvidedUserInfoToCreditStudyDto
    >(addUserInfoToCreditStudy, {
      errorPolicy: 'all',
      context: { clientName: AptunoApolloLink.CREDIT_STUDY },
    });

    const result: Response<string> = useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !!data?.addUserInfoToCreditStudy?.value
          ? new Response.Success(data.addUserInfoToCreditStudy.value)
          : Response.notPerformed,
      [data, error]
    );

    return {
      result,
      executing: loading,
      execute: (command: AddProvidedUserInfoToCreditStudyCommand) =>
        action({ variables: mapInitializeCreditStudyCommandToDto(command) }),
    };
  },

  useInitializeCreditStudy: (): BaseMutationResponse2<
    InitializeCreditStudyCommand,
    CreditStudyId
  > => {
    const [action, { loading, data, error }] =
      useMutation<InitializeCreditStudyResponseDto>(initializeCreditStudy, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

    const result: Response<CreditStudyId> = useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !!data?.initializeCreditStudy?.value
          ? new Response.Success(
              new CreditStudyId(data.initializeCreditStudy.value)
            )
          : Response.notPerformed,
      [data, error]
    );

    return {
      result,
      executing: loading,
      execute: () => action({ variables: undefined }),
    };
  },
};
